import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { authClient, useSession } from "@/lib/auth-client";
import { ROLE_DISPLAY_NAMES, type USGRole } from "@/lib/rbac";

const nameSchema = z.object({
  name: z.string().min(1, "Display name is required"),
});

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "At least 8 characters")
      .regex(/[A-Z]/, "At least one uppercase letter")
      .regex(/[0-9]/, "At least one number")
      .regex(/[^A-Za-z0-9]/, "At least one special character"),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type NameValues = z.infer<typeof nameSchema>;
type PasswordValues = z.infer<typeof passwordSchema>;

export function ProfileForm() {
  const { data: session, refetch } = useSession();
  const user = session?.user;
  const [nameLoading, setNameLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const nameForm = useForm<NameValues>({
    resolver: zodResolver(nameSchema),
    values: { name: user?.name ?? "" },
  });

  const passwordForm = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });

  async function onNameSubmit(values: NameValues) {
    setNameLoading(true);
    try {
      const { error } = await authClient.updateUser({ name: values.name });
      if (error) throw error;
      await refetch();
      toast.success("Display name updated");
    } catch {
      toast.error("Failed to update display name");
    } finally {
      setNameLoading(false);
    }
  }

  async function onPasswordSubmit(values: PasswordValues) {
    setPasswordLoading(true);
    try {
      const { error } = await authClient.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        revokeOtherSessions: true,
      });
      if (error) throw error;
      passwordForm.reset();
      toast.success("Password changed. Other sessions have been signed out.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  }

  const roleLabel = ROLE_DISPLAY_NAMES[(user?.role as USGRole) ?? "engineer"] ?? user?.role ?? "—";

  return (
    <div className="stack-16" style={{ maxWidth: 640 }}>
      {/* Read-only role info */}
      <div className="card">
        <div className="card__header">
          <h2 className="h-section">Account Info</h2>
          <span className="t-small t-muted">Managed by OPS</span>
        </div>
        <div className="card__body">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="t-label">Role</span>
              <p className="font-medium mt-1">{roleLabel}</p>
            </div>
            <div>
              <span className="t-label">Department</span>
              <p className="font-medium mt-1">
                {(user as { department?: string })?.department ?? "—"}
              </p>
            </div>
            <div>
              <span className="t-label">Cost Rate</span>
              <p className="font-medium mt-1">
                {(user as { costRate?: number })?.costRate != null
                  ? `$${((user as { costRate: number }).costRate).toFixed(2)}/hr`
                  : "—"}
              </p>
            </div>
            <div>
              <span className="t-label">Employee ID</span>
              <p className="font-medium mt-1">
                {(user as { employeeId?: string })?.employeeId ?? "—"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Display name */}
      <div className="card">
        <div className="card__header">
          <h2 className="h-section">Display Name</h2>
        </div>
        <div className="card__body">
          <Form {...nameForm}>
            <form onSubmit={nameForm.handleSubmit(onNameSubmit)} className="space-y-4">
              <FormField
                control={nameForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" disabled={nameLoading}>
                {nameLoading ? "Saving..." : "Update Name"}
              </Button>
            </form>
          </Form>
        </div>
      </div>

      {/* Change password */}
      <div className="card">
        <div className="card__header">
          <h2 className="h-section">Change Password</h2>
        </div>
        <div className="card__body">
          <Form {...passwordForm}>
            <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
              <FormField
                control={passwordForm.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Password</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={passwordForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={passwordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm New Password</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <p className="t-small t-muted">
                Changing your password will sign out all other active sessions.
              </p>
              <Button type="submit" disabled={passwordLoading}>
                {passwordLoading ? "Changing..." : "Change Password"}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
